function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import moment from "moment";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import Auth from "../../functions/authenticate";
import { INPUT, LABEL } from "../../components/form-styles";
import { PlunkDropDown } from "../../components/analytics-kit";
import { hasTenantFeature } from "../../functions/tenant-features";
import { NotEnabled } from "./common";
import { api, qs, errText, isNotEnabled, idStr, num, CLASSES, classLabel, dateTime, parseMaybe } from "./api";

/*
 * Outreach Replies inbox (2026-10-09 redesign, Replies.dc mockup). Same API as before:
 *   GET replies (classification / programId / unhandled; counts = UNHANDLED counts per class), GET replies/{id}
 *   (body, lead, our sent messages in the thread, any hand-off), POST {id}/reply {text} (same mailbox, same thread),
 *   {id}/classify (re-applies the stop rules), {id}/handled, {id}/handoff, {id}/snooze.
 *
 * Filter pills map onto the API's two axes (class x handled):
 *   Need you        = not handled yet, class interested | question | other
 *   Interested      = not handled yet, interested
 *   Questions       = not handled yet, question
 *   Done            = handled by a person (handled_date set: reply sent, marked done, reclassified, handed off, snoozed)
 *   Handled for you = not handled by a person, class ooo | unsubscribe | wrong_person | not_interested: the reply
 *                     handler already applied the stop rules (rescheduled, suppressed, stopped) on arrival.
 * The API filters one class at a time, so multi-class pills fetch each class and merge newest first.
 * Bounces never list here (the API excludes them).
 * No reply-drafting AI call exists in the Outreach API (compose/suggest writes marketing sections), so there is
 * no "Suggest a reply" button.
 */

const PAGE = 50;
const lbl = {
  ...LABEL,
  margin: "8px 0 4px"
};
const NEED = ["interested", "question", "other"];
const AUTO = ["ooo", "unsubscribe", "wrong_person", "not_interested"];
const TABS = [{
  key: "need",
  label: "Need you",
  classes: NEED,
  unhandled: true
}, {
  key: "interested",
  label: "Interested",
  classes: ["interested"],
  unhandled: true
}, {
  key: "question",
  label: "Questions",
  classes: ["question"],
  unhandled: true
}, {
  key: "done",
  label: "Done",
  classes: null,
  unhandled: false
}, {
  key: "auto",
  label: "Handled for you",
  classes: AUTO,
  unhandled: true
}];
const RUNNING = ["queued", "in_sequence", "ooo"];
const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
/** "morgan.reyes@x.com" → "Morgan Reyes" (the list rows carry no name). */
function nameFromEmail(email) {
  const local = String(email || "").split("@")[0];
  const parts = local.split(/[._\-+]+/).filter(p => p && !/^\d+$/.test(p));
  return parts.length ? parts.slice(0, 3).map(cap).join(" ") : email || "Unknown sender";
}
/** The lead's own name from its merge fields, when the list had one. */
function nameFromLead(lead) {
  const m = parseMaybe(lead && lead.mergeJson) || {};
  const g = (...ks) => {
    for (const k of ks) {
      if (m[k] && String(m[k]).trim()) return String(m[k]).trim();
    }
    return "";
  };
  const full = g("name", "full_name", "fullName");
  const first = g("first_name", "firstName");
  const last = g("last_name", "lastName");
  return first || last ? `${first} ${last}`.trim() : full;
}
const initials = name => String(name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join("") || "?";
function when(d) {
  if (!d) return "";
  const m = moment(d);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return m.format("h:mm A");
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.isSame(moment(), "year") ? m.format("MMM D") : m.format("MMM D, YYYY");
}
const tone = c => c === "interested" ? "good" : c === "question" ? "info" : "plain";
const snoozedUntil = r => String(r.handledBy || "").indexOf("snooze:") === 0 && r.handledDate && moment(r.handledDate).isAfter(moment()) ? r.handledDate : null;

/** A small "···" menu; outside clicks close it (ref containment, not stopPropagation). */
class Menu extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "away", e => {
      if (this.ref.current && !this.ref.current.contains(e.target)) this.close();
    });
    _defineProperty(this, "toggle", () => {
      const open = !this.state.open;
      this.setState({
        open
      });
      if (open) document.addEventListener("mousedown", this.away);else document.removeEventListener("mousedown", this.away);
    });
    _defineProperty(this, "close", () => {
      this.setState({
        open: false
      });
      document.removeEventListener("mousedown", this.away);
    });
    this.state = {
      open: false
    };
    this.ref = /*#__PURE__*/React.createRef();
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.away);
  }
  render() {
    const {
      items,
      label = "More actions",
      disabled
    } = this.props;
    return /*#__PURE__*/React.createElement("span", {
      className: "ori-menu",
      ref: this.ref
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-btn ori-more",
      "aria-label": label,
      title: label,
      disabled: disabled,
      onClick: this.toggle
    }, "\xB7\xB7\xB7"), this.state.open ? /*#__PURE__*/React.createElement("div", {
      className: "ori-menu-pop",
      role: "menu"
    }, items.filter(Boolean).map((it, i) => it.sep ? /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "ori-menu-sep"
    }, it.sep) : /*#__PURE__*/React.createElement("button", {
      key: i,
      type: "button",
      role: "menuitem",
      className: it.danger ? "danger" : "",
      onClick: () => {
        this.close();
        it.onClick();
      }
    }, it.label))) : null);
  }
}
class OutreachRepliesComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "load", async (skip = 0) => {
      const seq = ++this._seq;
      const {
        tab,
        programId
      } = this.state;
      const t = TABS.find(x => x.key === tab) || TABS[0];
      try {
        const doneP = api("GET", `/replies${qs({
          programId,
          unhandled: false,
          $top: 1
        })}`).catch(() => null);
        let items, total, counts;
        if (!t.classes || t.classes.length === 1) {
          const list = await api("GET", `/replies${qs({
            classification: t.classes ? t.classes[0] : "",
            programId,
            unhandled: t.unhandled,
            $top: PAGE,
            $skip: skip
          })}`);
          items = list && list.items || [];
          total = Number(list && list.total) || 0;
          counts = list && list.counts || {};
        } else {
          // one class per call: fetch each up to the end of this page, merge newest first, slice the page
          const lists = await Promise.all(t.classes.map(c => api("GET", `/replies${qs({
            classification: c,
            programId,
            unhandled: t.unhandled,
            $top: skip + PAGE,
            $skip: 0
          })}`)));
          const all = [];
          lists.forEach(l => (l && l.items || []).forEach(x => all.push(x)));
          all.sort((a, b) => String((b.reply || {}).receivedDate || "").localeCompare(String((a.reply || {}).receivedDate || "")));
          items = all.slice(skip, skip + PAGE);
          total = lists.reduce((a, l) => a + (Number(l && l.total) || 0), 0);
          counts = lists[0] && lists[0].counts || {};
        }
        const done = await doneP;
        if (!this._alive || seq !== this._seq) return;
        this.setState({
          items,
          total,
          counts,
          skip,
          error: null,
          doneTotal: done ? Number(done.total) || 0 : this.state.doneTotal
        });
        if (!this.state.sel && items.length) this.open(items[0])();
      } catch (e) {
        if (!this._alive || seq !== this._seq) return;
        if (isNotEnabled(e)) this.setState({
          notEnabled: true
        });else this.setState({
          error: errText(e, "Replies could not be loaded."),
          items: this.state.items || []
        });
      }
    });
    _defineProperty(this, "open", row => async () => {
      const id = idStr((row.reply || {}).id);
      this.setState({
        sel: id,
        detail: null,
        reply: "",
        handoff: null,
        actErr: null,
        actOk: null
      });
      try {
        const d = await api("GET", `/replies/${id}`);
        if (this._alive && this.state.sel === id) this.setState({
          detail: d
        });
      } catch (e) {
        if (this._alive) this.setState({
          actErr: errText(e, "This reply could not be loaded.")
        });
      }
    });
    _defineProperty(this, "act", (key, fn, ok) => async () => {
      this.setState({
        busy: key,
        actErr: null,
        actOk: null
      });
      try {
        const r = await fn();
        if (!this._alive) return;
        this.setState({
          busy: null,
          actOk: typeof ok === "function" ? ok(r) : ok
        });
        const sel = this.state.sel;
        await this.load(this.state.skip);
        if (sel) {
          try {
            const d = await api("GET", `/replies/${sel}`);
            if (this._alive && this.state.sel === sel) this.setState({
              detail: d
            });
          } catch (e) {/* listed elsewhere now */}
        }
      } catch (e) {
        if (this._alive) this.setState({
          busy: null,
          actErr: errText(e, "That didn't work. Try again.")
        });
      }
    });
    _defineProperty(this, "setTab", tab => this.setState({
      tab,
      sel: null,
      detail: null,
      items: null
    }, () => this.load(0)));
    _defineProperty(this, "setProgram", programId => this.setState({
      programId,
      sel: null,
      detail: null,
      items: null
    }, () => this.load(0)));
    this.state = {
      items: null,
      total: 0,
      counts: {},
      doneTotal: null,
      programs: [],
      tab: "need",
      programId: (() => {
        try {
          return new URLSearchParams(props.location && props.location.search || "").get("program") || "";
        } catch (e) {
          return "";
        }
      })(),
      skip: 0,
      sel: null,
      detail: null,
      error: null,
      notEnabled: false,
      reply: "",
      handoff: null,
      busy: null,
      actErr: null,
      actOk: null
    };
    this._alive = true;
    this._seq = 0;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    this.load();
    api("GET", `/programs${qs({
      $top: 200
    })}`).then(r => this._alive && this.setState({
      programs: (r && r.items || []).map(x => x.program || {})
    })).catch(() => null);
  }
  componentWillUnmount() {
    this._alive = false;
  }
  // ---------------------------------------------------------------- conversation

  renderDetail() {
    const {
      detail,
      sel,
      reply,
      handoff,
      busy,
      actErr,
      actOk
    } = this.state;
    if (!sel) return /*#__PURE__*/React.createElement("div", {
      className: "ori-blank"
    }, "Pick a reply on the left to read it and answer.");
    if (!detail) return actErr ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, actErr) : /*#__PURE__*/React.createElement("div", {
      className: "ori-blank"
    }, "Loading\u2026");
    const row = detail.row || {};
    const r = row.reply || {};
    const lead = detail.lead || {};
    const id = idStr(r.id);
    const email = r.fromEmail || row.leadEmail || lead.email || "";
    const name = nameFromLead(lead) || nameFromEmail(email);
    const first = name.split(/\s+/)[0] || name;
    const h = detail.handoff;
    const mailbox = row.mailboxAddress || "";
    const thread = (detail.thread || []).slice().sort((a, b) => String(a.sentDate || "").localeCompare(String(b.sentDate || "")));
    const steps = thread.filter(m => Number(m.stepOrdinal) > 0);
    const repliedTo = steps.find(m => m.messageId && r.inReplyTo && m.messageId === r.inReplyTo) || steps.filter(m => !r.receivedDate || String(m.sentDate || "") <= String(r.receivedDate)).pop();
    const before = thread.filter(m => !r.receivedDate || String(m.sentDate || "") <= String(r.receivedDate));
    const after = thread.filter(m => r.receivedDate && String(m.sentDate || "") > String(r.receivedDate));
    const snoozed = snoozedUntil(r);
    const isDone = !!r.handledDate && !snoozed;
    const running = RUNNING.indexOf(lead.status || row.leadStatus) > -1;
    const ours = m => /*#__PURE__*/React.createElement("div", {
      key: idStr(m.id),
      className: "ori-msg"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ori-msg-meta"
    }, "You, ", when(m.sentDate), " \xB7 ", Number(m.stepOrdinal) > 0 ? `email ${m.stepOrdinal}` : "your reply", mailbox ? ` · from ${mailbox}` : "", m.status && m.status !== "sent" ? ` · ${m.status}` : "", m.openedDate ? ` · opened ${when(m.openedDate)}` : "", m.clickedDate ? ` · clicked ${when(m.clickedDate)}` : ""), /*#__PURE__*/React.createElement("b", null, m.subject || "(no subject)"), m.error ? /*#__PURE__*/React.createElement("span", {
      className: "ori-msg-err"
    }, m.error) : null);
    const handedTo = h && (h.repName || h.repPersonId && String(h.repPersonId) !== "0") ? h.repName || `person ${idStr(h.repPersonId)}` : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "ori-convo"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-convo-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-convo-who"
    }, /*#__PURE__*/React.createElement("b", null, name), /*#__PURE__*/React.createElement("span", null, email, row.programName ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: `/Email/campaigns/outreach/${encodeURIComponent(row.programId)}`
    }, row.programName)) : null, repliedTo ? ` · replied to email ${repliedTo.stepOrdinal}` : "")), r.classification !== "not_interested" ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-btn",
      disabled: !!busy,
      onClick: this.act("ni", () => api("POST", `/replies/${id}/classify`, {
        classification: "not_interested"
      }), "Marked not interested. They won't get any more emails from this campaign.")
    }, busy === "ni" ? "Saving…" : "Not interested") : null, isDone ? /*#__PURE__*/React.createElement("span", {
      className: "ori-done-tag"
    }, "\u2713 Done") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-btn",
      disabled: !!busy,
      onClick: this.act("handled", () => api("POST", `/replies/${id}/handled`), "Marked done.")
    }, busy === "handled" ? "Saving…" : "Mark done"), /*#__PURE__*/React.createElement(Menu, {
      disabled: !!busy,
      items: [{
        label: handoff ? "Close hand-off" : "Hand off to a teammate",
        onClick: () => this.setState({
          handoff: handoff ? null : {
            repName: "",
            repPersonId: "",
            note: ""
          }
        })
      }, {
        sep: "Remind me later"
      }, ...[1, 3, 7].map(d => ({
        label: `In ${d} day${d === 1 ? "" : "s"}`,
        onClick: this.act("snooze", () => api("POST", `/replies/${id}/snooze`, {
          days: d
        }), x => `Hidden until ${dateTime(x && x.until)}.`)
      })), {
        sep: "Wrong category? Change it to"
      }, ...CLASSES.filter(c => c !== r.classification && c !== "bounce").map(c => ({
        label: classLabel(c),
        onClick: this.act(`cls-${c}`, () => api("POST", `/replies/${id}/classify`, {
          classification: c
        }), `Changed to ${classLabel(c)}.`)
      }))]
    })), snoozed ? /*#__PURE__*/React.createElement("div", {
      className: "ori-note"
    }, "Hidden until ", dateTime(snoozed), ". It comes back to Need you then.") : null, r.oooUntil ? /*#__PURE__*/React.createElement("div", {
      className: "ori-note"
    }, "Out of office until ", dateTime(r.oooUntil), ".") : null, handedTo ? /*#__PURE__*/React.createElement("div", {
      className: "ori-note"
    }, "Handed off to ", /*#__PURE__*/React.createElement("b", null, handedTo), h.note ? `: "${h.note}"` : "") : null, /*#__PURE__*/React.createElement("div", {
      className: "ori-thread"
    }, before.map(ours), /*#__PURE__*/React.createElement("div", {
      className: "ori-msg theirs"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ori-msg-meta"
    }, first, ", ", when(r.receivedDate), r.receivedDate && !moment(r.receivedDate).isSame(moment(), "day") ? ` ${moment(r.receivedDate).format("h:mm A")}` : ""), /*#__PURE__*/React.createElement("div", {
      className: "ori-body"
    }, r.bodyText || r.snippet || "(empty message)")), after.map(ours)), handoff ? /*#__PURE__*/React.createElement("div", {
      className: "ori-panel"
    }, /*#__PURE__*/React.createElement("b", null, "Hand off to a teammate"), /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, "They get a note and the person moves to prospect. No more campaign emails go to ", first, "."), /*#__PURE__*/React.createElement("div", {
      className: "or-form"
    }, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Teammate's name"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: handoff.repName,
      onChange: e => this.setState({
        handoff: {
          ...handoff,
          repName: e.target.value
        }
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Their contact id (optional)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: handoff.repPersonId,
      placeholder: "Plunk contact id",
      onChange: e => this.setState({
        handoff: {
          ...handoff,
          repPersonId: e.target.value.replace(/[^0-9]/g, "")
        }
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "wide"
    }, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Note for them"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: handoff.note,
      onChange: e => this.setState({
        handoff: {
          ...handoff,
          note: e.target.value
        }
      })
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy || !handoff.repName.trim(),
      onClick: this.act("handoff", () => api("POST", `/replies/${id}/handoff`, {
        repPersonId: handoff.repPersonId || "0",
        repName: handoff.repName.trim(),
        note: handoff.note.trim() || undefined
      }), () => {
        this.setState({
          handoff: null
        });
        return "Handed off.";
      })
    }, busy === "handoff" ? "Handing off…" : "Hand off"), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.setState({
        handoff: null
      })
    }, "Cancel"))) : null, /*#__PURE__*/React.createElement("div", {
      className: "ori-compose"
    }, /*#__PURE__*/React.createElement("textarea", {
      "aria-label": "Your reply",
      value: reply,
      onChange: e => this.setState({
        reply: e.target.value
      }),
      placeholder: `Write your reply to ${first}`
    }), /*#__PURE__*/React.createElement("div", {
      className: "ori-compose-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, "Sends from ", mailbox || "the same mailbox", " in the same thread. ", running ? `The sequence is still running for ${first}.` : `The rest of the sequence has stopped for ${first}.`), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy || !reply.trim(),
      onClick: this.act("reply", () => api("POST", `/replies/${id}/reply`, {
        text: reply
      }), () => {
        this.setState({
          reply: ""
        });
        return "Reply sent.";
      })
    }, busy === "reply" ? "Sending…" : "Send reply"))), actErr ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, actErr) : null, actOk ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, actOk) : null);
  }
  emptyText() {
    switch (this.state.tab) {
      case "need":
        return "Nothing needs you right now. Interested people and questions show up here first.";
      case "interested":
        return "No interested replies waiting.";
      case "question":
        return "No questions waiting.";
      case "done":
        return "Nothing done yet. Replies you answer or mark done move here.";
      case "auto":
        return "Nothing here yet. Out-of-office notes, unsubscribes and wrong-person replies are handled for you and show up here.";
      default:
        return "No replies.";
    }
  }
  render() {
    if (!hasTenantFeature("outreach") || this.state.notEnabled) return /*#__PURE__*/React.createElement(NotEnabled, {
      title: "Outreach replies"
    });
    const {
      items,
      total,
      counts,
      doneTotal,
      programs,
      tab,
      programId,
      sel,
      skip,
      error
    } = this.state;
    const c = k => Number(counts[k]) || 0;
    const sum = ks => ks.reduce((a, k) => a + c(k), 0);
    const needN = sum(NEED);
    const pillN = {
      need: needN,
      interested: c("interested"),
      question: c("question"),
      done: doneTotal,
      auto: sum(AUTO)
    };
    const progOpts = [["", "All campaigns"]].concat(programs.map(p => [p.id, p.name]));
    return /*#__PURE__*/React.createElement("div", {
      className: "ori-page"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-top"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Email/outreach",
      className: "ori-back",
      "aria-label": "Back to Outreach",
      title: "Back to Outreach"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M15 18l-6-6 6-6"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-title"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "Replies"), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, items === null ? "Loading…" : `${num(needN)} need you · out-of-office and unsubscribes are handled for you`)), /*#__PURE__*/React.createElement("span", {
      className: "ori-grow"
    }), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: progOpts,
      value: programId,
      onChange: this.setProgram,
      width: "220px"
    })), error ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger ori-banner"
    }, error) : null, /*#__PURE__*/React.createElement("div", {
      className: "ori-split"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-list"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-pills",
      role: "tablist",
      "aria-label": "Reply filters"
    }, TABS.map(t => /*#__PURE__*/React.createElement("button", {
      key: t.key,
      type: "button",
      role: "tab",
      "aria-selected": tab === t.key,
      className: `ori-pill${tab === t.key ? " on" : ""}`,
      onClick: () => this.setTab(t.key)
    }, t.label, pillN[t.key] != null ? ` ${num(pillN[t.key])}` : ""))), /*#__PURE__*/React.createElement("div", {
      className: "ori-rows"
    }, items === null ? /*#__PURE__*/React.createElement("div", {
      className: "ori-blank"
    }, "Loading\u2026") : !items.length ? /*#__PURE__*/React.createElement("div", {
      className: "ori-blank"
    }, this.emptyText()) : items.map(row => {
      const r = row.reply || {};
      const id = idStr(r.id);
      const email = r.fromEmail || row.leadEmail;
      const name = nameFromEmail(email);
      const snoozed = snoozedUntil(r);
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: id,
        className: `ori-item${sel === id ? " on" : ""}`,
        onClick: this.open(row)
      }, /*#__PURE__*/React.createElement("span", {
        className: `ori-av ${tone(r.classification)}`
      }, initials(name)), /*#__PURE__*/React.createElement("span", {
        className: "ori-item-main"
      }, /*#__PURE__*/React.createElement("span", {
        className: "ori-item-top"
      }, /*#__PURE__*/React.createElement("b", null, name), /*#__PURE__*/React.createElement("span", {
        className: "ori-when"
      }, when(r.receivedDate))), /*#__PURE__*/React.createElement("span", {
        className: `ori-cat ${tone(r.classification)}`
      }, classLabel(r.classification), snoozed ? ` · back ${when(snoozed)}` : "", programId ? "" : row.programName ? /*#__PURE__*/React.createElement("span", {
        className: "ori-camp"
      }, " \xB7 ", row.programName) : null), /*#__PURE__*/React.createElement("span", {
        className: "ori-snip"
      }, r.snippet || "")));
    })), items && total > PAGE ? /*#__PURE__*/React.createElement("div", {
      className: "ori-pager"
    }, /*#__PURE__*/React.createElement("span", null, num(skip + 1), " to ", num(skip + items.length), " of ", num(total)), /*#__PURE__*/React.createElement("span", {
      className: "ori-grow"
    }), skip > 0 ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-btn",
      onClick: () => this.load(Math.max(0, skip - PAGE))
    }, "Newer") : null, skip + items.length < total ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-btn",
      onClick: () => this.load(skip + PAGE)
    }, "Older") : null) : null), /*#__PURE__*/React.createElement("div", {
      className: "ori-detail"
    }, this.renderDetail())));
  }
}
export default withRouter(OutreachRepliesComponent);